/**
 * 项目组管理页面
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  UserGroupIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowRightOnRectangleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  UsersIcon,
  FolderIcon,
  StarIcon,
  ArrowPathIcon,
  ClipboardDocumentIcon,
} from '@heroicons/react/24/outline';
import { projectGroupApi, usersApi, invitationApi } from '../services/api';
import Toast from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';

interface Member {
  user_id: number;
  username: string;
  display_name: string;
  joined_at: string | null;
}

interface ProjectGroup {
  id: string;
  name: string;
  description: string | null;
  owner_id: number | null;
  owner_name: string | null;
  created_by: number | null;
  is_default: boolean;
  member_count: number;
  project_count: number;
  members: Member[];
  my_role: string; // "owner" | "member"
  created_at: string | null;
}

interface UserItem {
  id: number;
  username: string;
  display_name: string;
  role: string;
}

// 生成随机背景色（用于用户头像）
const getRandomColor = (id: number): string => {
  const colors = [
    'bg-red-500', 'bg-orange-500', 'bg-amber-500', 'bg-yellow-500',
    'bg-lime-500', 'bg-green-500', 'bg-emerald-500', 'bg-teal-500',
    'bg-cyan-500', 'bg-sky-500', 'bg-blue-500', 'bg-indigo-500',
    'bg-violet-500', 'bg-purple-500', 'bg-fuchsia-500', 'bg-pink-500',
    'bg-rose-500',
  ];
  return colors[id % colors.length];
};

const ProjectGroupManagement: React.FC = () => {
  const [groups, setGroups] = useState<ProjectGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState<UserItem[]>([]);
  const [searchUsers, setSearchUsers] = useState<UserItem[]>([]); // 搜索结果
  const [isSearching, setIsSearching] = useState(false); // 搜索中状态

  // Toast
  const [toast, setToast] = useState<{ show: boolean; type: 'success' | 'error' | 'warning' | 'info'; message: string }>({
    show: false, type: 'success', message: ''
  });

  // 确认对话框
  const [confirmDialog, setConfirmDialog] = useState<{
    show: boolean; title: string; message: string; onConfirm: () => void;
  }>({ show: false, title: '', message: '', onConfirm: () => {} });

  // 创建/编辑项目组弹窗
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ProjectGroup | null>(null);
  const [groupForm, setGroupForm] = useState({ name: '', description: '', owner_id: 0 });
  const [formError, setFormError] = useState('');

  // 成员管理弹窗
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [memberModalGroup, setMemberModalGroup] = useState<ProjectGroup | null>(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [pendingMemberIds, setPendingMemberIds] = useState<number[]>([]);
  const [pendingUsers, setPendingUsers] = useState<UserItem[]>([]); // 新添加用户的完整信息
  const [sentInvitations, setSentInvitations] = useState<any[]>([]); // 已发送的邀请
  const [isSendingInvite, setIsSendingInvite] = useState<number | null>(null); // 正在发送邀请的用户ID

  // 详情弹窗
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailGroup, setDetailGroup] = useState<ProjectGroup | null>(null);

  const showToast = (type: 'success' | 'error' | 'warning' | 'info', message: string) => {
    setToast({ show: true, type, message });
  };

  const loadGroups = useCallback(async () => {
    try {
      setLoading(true);
      const res = await projectGroupApi.getList();
      setGroups(res.data || []);
    } catch (e: any) {
      showToast('error', '加载项目组失败');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAllUsers = useCallback(async () => {
    try {
      const res = await usersApi.getList();
      setAllUsers(res.data || []);
    } catch {
      // 非管理员获取不到所有用户，静默处理
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  // ---- 创建/编辑 ----
  const openCreateModal = () => {
    setEditingGroup(null);
    setGroupForm({ name: '', description: '', owner_id: 0 });
    setFormError('');
    loadAllUsers(); // 按需加载用户列表（仅管理员可见负责人选择）
    setShowGroupModal(true);
  };

  const openEditModal = (group: ProjectGroup) => {
    setEditingGroup(group);
    setGroupForm({
      name: group.name,
      description: group.description || '',
      owner_id: group.owner_id || 0,
    });
    setFormError('');
    loadAllUsers(); // 按需加载用户列表
    setShowGroupModal(true);
  };

  const handleSaveGroup = async () => {
    if (!groupForm.name.trim()) {
      setFormError('请输入项目组名称');
      return;
    }
    try {
      if (editingGroup) {
        await projectGroupApi.update(editingGroup.id, {
          name: groupForm.name.trim(),
          description: groupForm.description.trim() || undefined,
          owner_id: groupForm.owner_id || undefined,
        });
        showToast('success', '项目组已更新');
      } else {
        await projectGroupApi.create({
          name: groupForm.name.trim(),
          description: groupForm.description.trim() || undefined,
          owner_id: groupForm.owner_id || undefined,
        });
        showToast('success', '项目组已创建');
      }
      setShowGroupModal(false);
      loadGroups();
    } catch (e: any) {
      showToast('error', e?.response?.data?.detail || '操作失败');
    }
  };

  // ---- 删除 ----
  const handleDelete = (group: ProjectGroup) => {
    if (group.is_default) {
      showToast('warning', '默认项目组不可删除');
      return;
    }
    setConfirmDialog({
      show: true,
      title: '确认删除项目组？',
      message: `确定要删除项目组「${group.name}」吗？${group.project_count > 0 ? `该组下有 ${group.project_count} 个项目，请先将项目移出或删除。` : '此操作不可恢复。'}`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, show: false }));
        try {
          await projectGroupApi.delete(group.id);
          showToast('success', '项目组已删除');
          loadGroups();
        } catch (e: any) {
          showToast('error', e?.response?.data?.detail || '删除失败');
        }
      }
    });
  };

  // ---- 退出 ----
  const handleLeave = (group: ProjectGroup) => {
    setConfirmDialog({
      show: true,
      title: '确认退出项目组？',
      message: `确定要退出项目组「${group.name}」吗？`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, show: false }));
        try {
          await projectGroupApi.leave(group.id);
          showToast('success', '已成功退出项目组');
          loadGroups();
        } catch (e: any) {
          showToast('error', e?.response?.data?.detail || '退出失败');
        }
      }
    });
  };

  // ---- 成员管理 ----
  const openMemberModal = async (group: ProjectGroup) => {
    setMemberModalGroup(group);
    setPendingMemberIds(group.members.map(m => m.user_id));
    setPendingUsers([]); // 清空新添加用户列表
    setMemberSearch('');
    setSearchUsers([]);
    setShowMemberModal(true);
    
    // 加载已发送的邀请
    try {
      const res = await invitationApi.getSentInvitations();
      // 只显示当前项目组的邀请
      const groupInvites = (res.data || []).filter((inv: any) => inv.group_id === group.id);
      setSentInvitations(groupInvites);
    } catch {
      setSentInvitations([]);
    }
  };

  // 搜索用户（使用新接口，无需管理员权限）
  const handleSearchUsers = useCallback(async (keyword: string) => {
    if (!keyword.trim()) {
      setSearchUsers([]);
      return;
    }
    setIsSearching(true);
    try {
      const res = await usersApi.search(keyword);
      // 过滤掉已经是成员的用户和负责人
      const existingIds = memberModalGroup?.members.map(m => m.user_id) || [];
      const ownerId = memberModalGroup?.owner_id || memberModalGroup?.created_by;
      const filtered = (res.data || []).filter((u: UserItem) =>
        !existingIds.includes(u.id) && u.id !== ownerId
      );
      setSearchUsers(filtered);
    } catch {
      setSearchUsers([]);
    } finally {
      setIsSearching(false);
    }
  }, [memberModalGroup]);

  // 防抖搜索
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearchUsers(memberSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [memberSearch, handleSearchUsers]);

  const handleSendInvite = async (user: UserItem) => {
    if (!memberModalGroup) return;
    const ownerId = memberModalGroup.owner_id || memberModalGroup.created_by;
    if (user.id === ownerId) return;
    
    // 检查是否已发送邀请
    const alreadyInvited = sentInvitations.some(inv => inv.user_id === user.id);
    if (alreadyInvited) {
      showToast('warning', '已发送过邀请');
      return;
    }
    
    setIsSendingInvite(user.id);
    try {
      const res = await invitationApi.send(memberModalGroup.id, user.id);
      const inviteCode = res.data.invite_code;
      const inviteLink = `${window.location.origin}/invite?code=${inviteCode}`;
      showToast('success', `邀请已发送！可分享链接：${inviteLink}`);
      
      // 添加到已邀请列表
      setSentInvitations(prev => [...prev, {
        user_id: user.id,
        user_name: user.display_name,
        invited_at: new Date().toISOString(),
        status: 'pending',
        invite_code: inviteCode
      }]);
      
      // 从搜索结果中移除
      setSearchUsers(prev => prev.filter(u => u.id !== user.id));
    } catch (e: any) {
      showToast('error', e?.response?.data?.detail || '发送邀请失败');
    } finally {
      setIsSendingInvite(null);
    }
  };

  const handleRemovePendingMember = (userId: number) => {
    if (!memberModalGroup) return;
    const ownerId = memberModalGroup.owner_id || memberModalGroup.created_by;
    if (userId === ownerId) return;
    setPendingMemberIds(prev => prev.filter(id => id !== userId));
    setPendingUsers(prev => prev.filter(u => u.id !== userId)); // 同步清除
  };

  // 删除已有成员（带确认提示）
  const handleRemoveExistingMember = (member: Member) => {
    if (!memberModalGroup) return;
    const ownerId = memberModalGroup.owner_id || memberModalGroup.created_by;
    if (member.user_id === ownerId) {
      showToast('warning', '不能删除负责人');
      return;
    }
    setConfirmDialog({
      show: true,
      title: '确认删除成员？',
      message: `确定要将「${member.display_name}」从项目组中移除吗？`,
      onConfirm: async () => {
        setConfirmDialog(prev => ({ ...prev, show: false }));
        try {
          await projectGroupApi.removeMember(memberModalGroup.id, member.user_id);
          showToast('success', '成员已移除');
          // 更新本地数据
          setMemberModalGroup(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              members: prev.members.filter(m => m.user_id !== member.user_id)
            };
          });
          setPendingMemberIds(prev => prev.filter(id => id !== member.user_id));
        } catch (e: any) {
          showToast('error', e?.response?.data?.detail || '移除成员失败');
        }
      }
    });
  };

  const handleSaveMembers = async () => {
    if (!memberModalGroup) return;
    try {
      await projectGroupApi.updateMembers(memberModalGroup.id, pendingMemberIds);
      showToast('success', '成员已更新');
      setShowMemberModal(false);
      loadGroups();
    } catch (e: any) {
      showToast('error', e?.response?.data?.detail || '更新成员失败');
    }
  };

  // ---- 详情 ----
  const openDetailModal = (group: ProjectGroup) => {
    setDetailGroup(group);
    setShowDetailModal(true);
  };

  const getRoleTag = (group: ProjectGroup) => {
    if (group.my_role === 'owner') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 rounded-full">
          <StarIcon className="w-3 h-3" />主
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold bg-gray-100 text-gray-600 rounded-full">
        辅
      </span>
    );
  };

  return (
    <div className="flex-1 flex flex-col min-w-0 bg-transparent">
      {/* 顶部标题栏 */}
      <div className="px-6 pt-4 pb-2 flex items-center justify-between flex-shrink-0">
        <div>
          <div className="flex items-center gap-3">
            <UserGroupIcon className="w-6 h-6 text-blue-600" />
            <h1 className="text-xl font-semibold text-gray-900">项目组管理</h1>
          </div>
          <p className="text-sm text-gray-500 mt-1 ml-9">创建和管理项目组，邀请成员协作</p>
        </div>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 font-medium text-sm"
        >
          <PlusIcon className="w-4 h-4" />
          新建项目组
        </button>
      </div>

      {/* 渐变分割线 */}
      <div className="mx-6 mt-2 h-px bg-gradient-to-r from-blue-400 via-indigo-500 to-blue-400 rounded-full" />

      {/* 内容区 */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <ArrowPathIcon className="w-8 h-8 animate-spin mb-3" />
            <p className="text-base">加载中...</p>
          </div>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <UserGroupIcon className="w-12 h-12 mb-3" />
            <p className="text-base">暂无项目组</p>
            <p className="text-sm mt-1">点击右上角「新建项目组」开始</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {[...groups]
              .sort((a, b) => (b.is_default ? 1 : 0) - (a.is_default ? 1 : 0))
              .map(group => (
              <div key={group.id} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-5 flex flex-col gap-3 relative">
                {/* 右上角角色标签 */}
                <div className="absolute top-3 right-3 flex items-center gap-1">
                  {group.is_default && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">默认</span>
                  )}
                  {getRoleTag(group)}
                </div>

                {/* 标题 */}
                <div className="pr-20">
                  <h2 className="text-base font-semibold text-gray-900 truncate">{group.name}</h2>
                  {group.description && (
                    <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{group.description}</p>
                  )}
                </div>

                {/* 统计信息 */}
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <UsersIcon className="w-4 h-4" />
                    <span>{group.member_count} 人</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <FolderIcon className="w-4 h-4" />
                    <span>{group.project_count} 个项目</span>
                  </div>
                </div>

                {/* 负责人 */}
                <div className="text-sm text-gray-600">
                  <span className="text-gray-400">负责人：</span>
                  <span className="font-medium">{group.owner_name || '未设置'}</span>
                </div>

                {/* 团队成员预览 */}
                {group.members.length > 0 && (
                  <div className="text-sm text-gray-600">
                    <span className="text-gray-400">团队：</span>
                    <span>
                      {group.members.slice(0, 4).map(m => m.display_name).join('、')}
                      {group.members.length > 4 && `...等${group.members.length}人`}
                    </span>
                  </div>
                )}

                {/* 操作按钮 */}
                <div className="flex items-center gap-2 pt-2 border-t border-gray-100 flex-wrap">
                  <button
                    onClick={() => openDetailModal(group)}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-50 border border-gray-200 rounded-lg transition-colors"
                  >
                    查看详情
                  </button>

                  {group.my_role === 'owner' ? (
                    <>
                      <button
                        onClick={() => openEditModal(group)}
                        className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors"
                        title="编辑项目组"
                      >
                        <PencilIcon className="w-3.5 h-3.5" />
                        编辑
                      </button>
                      <button
                        onClick={() => openMemberModal(group)}
                        className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-indigo-600 hover:bg-indigo-50 border border-indigo-200 rounded-lg transition-colors"
                        title="管理成员"
                      >
                        <UsersIcon className="w-3.5 h-3.5" />
                        成员
                      </button>
                      {!group.is_default && (
                        <button
                          onClick={() => handleDelete(group)}
                          className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors"
                          title="删除项目组"
                        >
                          <TrashIcon className="w-3.5 h-3.5" />
                          删除
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => handleLeave(group)}
                      className="flex items-center justify-center gap-1 px-2 py-1.5 text-xs text-orange-600 hover:bg-orange-50 border border-orange-200 rounded-lg transition-colors"
                    >
                      <ArrowRightOnRectangleIcon className="w-3.5 h-3.5" />
                      退出团队
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ====== 创建/编辑弹窗 ====== */}
      {showGroupModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">
                {editingGroup ? '编辑项目组' : '新建项目组'}
              </h3>
              <button onClick={() => setShowGroupModal(false)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">项目组名称 <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={groupForm.name}
                  onChange={e => setGroupForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="请输入项目组名称"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">备注描述</label>
                <textarea
                  value={groupForm.description}
                  onChange={e => setGroupForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="请输入项目组描述（可选）"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm resize-none"
                />
              </div>
              {allUsers.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">负责人</label>
                  <select
                    value={groupForm.owner_id}
                    onChange={e => setGroupForm(prev => ({ ...prev, owner_id: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                  >
                    <option value={0}>默认（当前用户）</option>
                    {allUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.display_name}（{u.username}）</option>
                    ))}
                  </select>
                </div>
              )}
              {formError && <p className="text-red-500 text-sm">{formError}</p>}
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <button onClick={() => setShowGroupModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors text-sm">取消</button>
              <button onClick={handleSaveGroup} className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors text-sm">
                {editingGroup ? '保存修改' : '创建'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ====== 成员管理弹窗（左右布局） ====== */}
      {showMemberModal && memberModalGroup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-base font-semibold text-gray-900">管理成员 - {memberModalGroup.name}</h3>
              <button onClick={() => setShowMemberModal(false)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* 左右布局主体 */}
            <div className="flex flex-1 overflow-hidden">
              {/* 左侧：搜索和搜索结果 */}
              <div className="w-1/2 border-r border-gray-200 flex flex-col">
                {/* 搜索框 */}
                <div className="p-4 border-b border-gray-100 flex-shrink-0">
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={memberSearch}
                      onChange={e => setMemberSearch(e.target.value)}
                      placeholder="搜索用户名或账号..."
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-sm"
                    />
                  </div>
                </div>

                {/* 搜索结果列表 */}
                <div className="flex-1 overflow-y-auto p-4">
                  {memberSearch.trim() ? (
                    isSearching ? (
                      <div className="text-center text-gray-400 text-sm py-8">搜索中...</div>
                    ) : searchUsers.length === 0 ? (
                      <div className="text-center text-gray-400 text-sm py-8">未找到匹配用户</div>
                    ) : (
                      <div className="space-y-2">
                        {searchUsers.map(u => {
                          const isAdded = pendingMemberIds.includes(u.id);
                          const isInvited = sentInvitations.some(inv => inv.user_id === u.id);
                          return (
                            <div
                              key={u.id}
                              className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-gray-100 hover:border-gray-200 bg-white"
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-medium ${getRandomColor(u.id)}`}>
                                  {u.display_name.charAt(0)}
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{u.display_name}</div>
                                  <div className="text-xs text-gray-500">{u.username}</div>
                                </div>
                              </div>
                              {isAdded ? (
                                <span className="text-xs text-green-600 font-medium px-2 py-1 bg-green-50 rounded">成员</span>
                              ) : isInvited ? (
                                <span className="text-xs text-orange-600 font-medium px-2 py-1 bg-orange-50 rounded">已邀请</span>
                              ) : (
                                <button
                                  onClick={() => handleSendInvite(u)}
                                  disabled={isSendingInvite === u.id}
                                  className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                                >
                                  {isSendingInvite === u.id ? '发送中...' : '邀请'}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )
                  ) : (
                    <div className="text-center text-gray-400 text-sm py-8">输入关键词搜索用户</div>
                  )}
                </div>
              </div>

              {/* 右侧：当前项目成员列表 + 已邀请 */}
              <div className="w-1/2 flex flex-col bg-gray-50">
                <div className="p-4 border-b border-gray-200 bg-white flex-shrink-0">
                  <div className="text-sm font-medium text-gray-700">项目组成员</div>
                  <div className="text-xs text-gray-400 mt-0.5">
                    成员 {pendingMemberIds.length} 人
                    {sentInvitations.length > 0 && `，已邀请 ${sentInvitations.length} 人`}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                  <div className="space-y-4">
                    {(() => {
                      const ownerId = memberModalGroup.owner_id || memberModalGroup.created_by;
                      const ownerMember = memberModalGroup.members.find(m => m.user_id === ownerId);
                      
                      // 其他原有成员（不包括负责人）
                      const otherExistingMembers = memberModalGroup.members.filter(m => m.user_id !== ownerId);
                      
                      return (
                        <>
                          {/* 负责人（置顶，不可操作） */}
                          {ownerMember && (
                            <div className="flex items-center justify-between px-3 py-3 rounded-lg bg-blue-50 border border-blue-100">
                              <div className="flex items-center gap-3">
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-white font-medium ${getRandomColor(ownerMember.user_id)}`}>
                                  {ownerMember.display_name.charAt(0)}
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {ownerMember.display_name}
                                    <span className="ml-1.5 text-xs text-blue-600">（负责人）</span>
                                  </div>
                                  <div className="text-xs text-gray-500">{ownerMember.username}</div>
                                </div>
                              </div>
                              <span className="text-xs text-gray-400">不可操作</span>
                            </div>
                          )}

                          {/* 其他成员 */}
                          {otherExistingMembers.map(member => (
                            <div
                              key={member.user_id}
                              className="flex items-center justify-between px-3 py-3 rounded-lg border bg-white border-gray-200"
                            >
                              <div className="flex items-center gap-3">
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-white font-medium ${getRandomColor(member.user_id)}`}>
                                  {member.display_name.charAt(0)}
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{member.display_name}</div>
                                  <div className="text-xs text-gray-500">{member.username}</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-green-600 font-medium">成员</span>
                                <button
                                  onClick={() => handleRemoveExistingMember(member)}
                                  className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="移除成员"
                                >
                                  <XMarkIcon className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ))}

                          {/* 已邀请（待确认） */}
                          {sentInvitations.length > 0 && (
                            <>
                              <div className="pt-2 border-t border-gray-200">
                                <div className="text-xs font-medium text-gray-500 mb-2">已邀请（待确认）</div>
                              </div>
                              {sentInvitations.map(invite => (
                                <div
                                  key={invite.user_id}
                                  className="flex items-center justify-between px-3 py-3 rounded-lg border bg-orange-50 border-orange-200"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-white font-medium ${getRandomColor(invite.user_id)}`}>
                                      {invite.user_name?.charAt(0) || '?'}
                                    </div>
                                    <div>
                                      <div className="text-sm font-medium text-gray-900">{invite.user_name || '未知用户'}</div>
                                      <div className="text-xs text-gray-500">
                                        {invite.invited_at ? new Date(invite.invited_at).toLocaleDateString() : ''} 邀请
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {invite.invite_code && (
                                      <button
                                        onClick={() => {
                                          const link = `${window.location.origin}/invite?code=${invite.invite_code}`;
                                          navigator.clipboard.writeText(link);
                                          showToast('success', '邀请链接已复制');
                                        }}
                                        className="text-xs text-blue-600 hover:text-blue-800"
                                        title="复制邀请链接"
                                      >
                                        复制链接
                                      </button>
                                    )}
                                    <span className="text-xs text-orange-600 font-medium">待确认</span>
                                  </div>
                                </div>
                              ))}
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex-shrink-0">
              <button onClick={() => setShowMemberModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors text-sm">关闭</button>
            </div>
          </div>
        </div>
      )}

      {/* ====== 详情弹窗 ====== */}
      {showDetailModal && detailGroup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-gray-900">{detailGroup.name}</h3>
                {detailGroup.is_default && (
                  <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">默认</span>
                )}
                {getRoleTag(detailGroup)}
              </div>
              <button onClick={() => setShowDetailModal(false)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {detailGroup.description && (
                <div>
                  <p className="text-sm text-gray-500">描述</p>
                  <p className="text-sm text-gray-800 mt-1">{detailGroup.description}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">负责人</p>
                  <p className="text-gray-900 font-medium mt-0.5">{detailGroup.owner_name || '未设置'}</p>
                </div>
                <div>
                  <p className="text-gray-500">成员数量</p>
                  <p className="text-gray-900 font-medium mt-0.5">{detailGroup.member_count} 人</p>
                </div>
                <div>
                  <p className="text-gray-500">关联项目</p>
                  <p className="text-gray-900 font-medium mt-0.5">{detailGroup.project_count} 个</p>
                </div>
                <div>
                  <p className="text-gray-500">创建时间</p>
                  <p className="text-gray-900 font-medium mt-0.5">
                    {detailGroup.created_at ? new Date(detailGroup.created_at).toLocaleDateString('zh-CN') : '-'}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-2">团队成员</p>
                <div className="flex flex-wrap gap-2">
                  {detailGroup.members.map(m => (
                    <span
                      key={m.user_id}
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        m.user_id === detailGroup.owner_id
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {m.display_name}
                      {m.user_id === detailGroup.owner_id && ' ★'}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex-shrink-0">
              <button onClick={() => setShowDetailModal(false)} className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg text-sm">关闭</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.message}
        duration={3000}
        onClose={() => setToast(prev => ({ ...prev, show: false }))}
      />

      {/* 确认对话框 */}
      <ConfirmDialog
        isOpen={confirmDialog.show}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="确定"
        cancelText="取消"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog(prev => ({ ...prev, show: false }))}
      />
    </div>
  );
};

export default ProjectGroupManagement;
